import { useState, useEffect, useCallback, useContext, useRef } from 'react'
import { useSelector, useDispatch, shallowEqual } from 'react-redux'
import { createUseStyles } from 'react-jss'
import { useLocation } from 'react-router-dom'
import CreateJs from 'preload-js'
import { Context as GeneralContext } from '@/context'
import { Context as LoadingContext } from '@/context/loading'
import Carousel from '@/components/Carousel'
import Hero from '@/components/Hero'
import LocomotiveScroll from '@/components/LocomotiveScroll'
import ScrollIcon from '@/components/ScrollIcon'
import RevealText from '@/components/RevealText'
import YoutubeVideo from '@/components/YoutubeVideo'
import Footer from '@/components/Footer'
import Meta from '@/components/Meta'
import useHandleAllMediaWithCb from '@/hooks/useHandleAllMediaWithCb'
import actions from '@/actions'
import { externalPreload } from '@/utils/dom'
import { getSlug } from '@/utils/path'
import style from './style'

const useStyles = createUseStyles(style)

const About = () => {
  const { headerHeight, getImageUrl } = useContext(GeneralContext)
  const { setReady, ready } = useContext(LoadingContext)
  const classes = useStyles({ headerHeight })
  const [load, setLoad] = useState(false)
  const preload = useRef(new CreateJs.LoadQueue())
  const $root = useRef()
  const location = useLocation()
  const stub = useRef(getSlug(location.pathname))

  /*------------------------------
  Hooks to handle media loading & updating the scrollbar
  ------------------------------*/
  useHandleAllMediaWithCb({
    init: load,
    ref: $root.current?.ref,
  })

  /*------------------------------
  Redux Connect
  ------------------------------*/
  const { current, page, mediaQueryKey } = useSelector((state) => ({
    current: state.page.current.find,
    mediaQueryKey: state.mediaQuery.key,
    page: state.page.pages[stub.current] || {},
    strings: state.language.string,
  }), shallowEqual)

  /*------------------------------
  Redux Actions
  ------------------------------*/
  const dispatch = useDispatch()
  const getPage = useCallback((index) => dispatch(actions.page.getDataPage(index)), [dispatch])
  const setCurrentPage = useCallback((index) => dispatch(actions.page.setCurrent(index)), [dispatch])

  /*------------------------------
  Complete Preload
  ------------------------------*/
  const completePreload = () => {
    externalPreload(setLoad)
    setReady(true)
  }

  /*------------------------------
  Set Gallery
  ------------------------------*/
  const setGallery = (imagesList) => {
    if (imagesList.length > 0) {
      const gal = imagesList.split(',')
      const imgSize = mediaQueryKey > 2 ? 'full' : 'large'
      const galleryItems = gal.map((i) => {
        // Controllo se esiste l'immagine su media_post
        if (page.media_post[i]) {
          const { width, height, caption } = page.media_post[i]
          const src = getImageUrl(i, imgSize, stub.current, 'pages')
          const ratio = height / width

          return {
            src,
            width,
            height,
            caption,
            ratio,
          }
        }
        return null
      })

      return galleryItems[0] ? galleryItems : []
    }
    return []
  }

  /*------------------------------
  Preload Main Images
  ------------------------------*/
  const preloadMedia = () => {
    preload.current.on('complete', completePreload)
    if (page.title_img_1 !== 0) preload.current.loadFile({ id: 'cover-img-1', src: getImageUrl(page.title_img_1, 'full', stub.current, 'pages') })
    if (page.title_img_2 !== 0) preload.current.loadFile({ id: 'cover-img-2', src: getImageUrl(page.title_img_2, 'full', stub.current, 'pages') })
  }

  /*------------------------------
  Initialize
  ------------------------------*/
  const initialize = () => {
    if (current !== stub.current) setCurrentPage(stub.current, 'pages')
    getPage(stub.current)
  }

  /*------------------------------
  If the page is loaded from redux, start to preload media
  ------------------------------*/
  useEffect(() => {
    if (Object.keys(page).length > 0) preloadMedia()
  }, [page])

  /*------------------------------
  Did Mount
  ------------------------------*/
  useEffect(() => {
    if (!load) initialize()
  }, [])

  /*------------------------------
  Render Helmet
  ------------------------------*/
  const renderHelmet = () => {
    return load && (
      <Meta
        title={page.yoast_meta.find((y) => y.property?.includes('title')).content || page.title.rendered}
        meta={page.yoast_meta}
        schema={page.yoast_json_ld}
      />
    )
  }

  /*------------------------------
  Render Cover
  ------------------------------*/
  const renderCover = () => {
    return page.title_with_images_list.length > 0 && (
      <>
        <Hero
          aboveTheFold={true}
          img={getImageUrl(page.featured_media, 'full', stub, 'pages')}
          title={page.title_with_images_list.map((t) => t.title_line).join('<br />')}
          fontSize="lg"
          viewportHeight={false}
          color="olive"
          img1={getImageUrl(page.title_img_1, 'large', stub.current, 'pages')}
          img2={getImageUrl(page.title_img_2, 'large', stub.current, 'pages')}
          showImage={load}
        />
        {mediaQueryKey <= 1 && (
          <div className={`${classes.wrapper} ${classes.imgWrapperMobile}`}>
            <img // eslint-disable-line
              src={getImageUrl(page.title_img_1, 'full', stub.current, 'pages')}
            />
            <img // eslint-disable-line
              src={getImageUrl(page.title_img_2, 'full', stub.current, 'pages')}
            />
          </div>
        )}
        <div className={classes.fullViewport}>
          <div className={classes.scrollIndicator}>
            <ScrollIcon
              show={ready}
              line
            />
          </div>
        </div>
      </>
    )
  }

  /*------------------------------
  Render Reveal Text
  ------------------------------*/
  const renderRevealText = () => {
    return page.content.rendered && (
      <div className={`${classes.wrapper} ${classes.wrapperDoublePaddingTop}`}>
        <RevealText
          className={classes.revealText}
          content={page.content.rendered}
        />
      </div>
    )
  }

  /*------------------------------
  Render Gallery
  ------------------------------*/
  const renderGallery = () => {
    const gallery = setGallery(page.project_gallery_id)
    return page.project_gallery_id && (
      <div className={classes.gallery}>
        <Carousel imagesList={gallery} />
      </div>
    )
  }

  /*------------------------------
  Render Column Text
  ------------------------------*/
  const renderColumnText = () => {
    return page.banner_single_text && (
      <div className={classes.wrapper}>
        <p
          className={classes.columnText}
          dangerouslySetInnerHTML={{ __html: page.banner_single_text }}
        />
      </div>
    )
  }

  /*------------------------------
  Render Video
  ------------------------------*/
  const renderVideo = () => {
    return page.video_content_url && (
      <div className={classes.wrapper}>
        <YoutubeVideo
          className={classes.video}
          videoId={page.video_content_url}
          poster={page.video_content_image || null}
        />
      </div>
    )
  }

  /*------------------------------
  Render Footer
  ------------------------------*/
  const renderFooter = () => {
    return <Footer />
  }

  /*------------------------------
  Render Content
  ------------------------------*/
  const renderContent = () => {
    return load && (
      <>
        {renderCover()}
        {renderRevealText()}
        {renderGallery()}
        {renderColumnText()}
        {renderVideo()}
        {renderFooter()}
      </>
    )
  }

  return (
    <LocomotiveScroll
      init={load}
      className={`page pageAbout ${classes.root}`}
      ref={$root}
    >
      {renderHelmet()}
      {renderContent()}
    </LocomotiveScroll>
  )
}

export default About
